import React, { useEffect, useRef, useState } from "react";
import { Box, Dialog, IconButton } from "@mui/material";
import Carousel from "react-multi-carousel";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RotateRightIcon from "@mui/icons-material/RotateRight"; // New Icon for Rotation
import ReactPlayer from "react-player";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { PUJALOADIMG } from "../../utils/Constants";
import ImageView from "../ImageView/ImageView";
import "./ImageModalCarousel.scss";

interface ImageModalCarouselProps {
  open: boolean;
  mediaArray: any[];
  selectedIndex: number;
  onClose: () => void;
}

const ImageModalCarousel: React.FC<ImageModalCarouselProps> = ({
  open,
  mediaArray,
  selectedIndex,
  onClose,
}) => {
  const modalCarouselRef = useRef<any>(null);
  const [currentIndex, setCurrentIndex] = useState(selectedIndex);
  // State to track rotation for each media item (image only)
  const [rotations, setRotations] = useState<number[]>(
    mediaArray.map(() => 0)
  );

  const transformRefs = useRef<any[]>([]);

  useEffect(() => {
    if (open && modalCarouselRef.current) {
      // Initialize rotation states based on mediaArray length when opening
      setRotations(mediaArray.map(() => 0));
      modalCarouselRef.current.goToSlide(selectedIndex);
      setCurrentIndex(selectedIndex);
    }
  }, [open, selectedIndex, mediaArray.length]);

  const handleAfterChange = (previousSlide: number, { currentSlide }: any) => {
    setCurrentIndex(currentSlide);
    // Reset zoom/pan for ALL elements except the one being viewed (including the one we just left)
    transformRefs.current.forEach((ref, index) => {
      if (ref) {
        ref.resetTransform();
        // Reset rotation only for the element we just left if needed, or rely on the effect for full reset
      }
    });
  };

  const handleThumbnailClick = (index: number) => {
    setCurrentIndex(index);
    modalCarouselRef.current?.goToSlide(index);
  };

  // New handler for rotation
  const handleRotate = () => {
    setRotations((prevRotations) => {
      const newRotations = [...prevRotations];
      // Increment rotation by 90 degrees, wrap around after 270 (back to 0)
      newRotations[currentIndex] = (newRotations[currentIndex] + 90) % 360;
      return newRotations;
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        className: "imageModalDialog",
      }}
      TransitionProps={{
        onEntered: () => {
          // Re-ensure correct state on transition enter
          modalCarouselRef.current?.goToSlide(selectedIndex);
          setCurrentIndex(selectedIndex);
          setRotations(mediaArray.map(() => 0)); // Reset rotations on open
        },
      }}
    >
      <IconButton onClick={onClose} className="closeButton">
        <CloseIcon fontSize="large" />
      </IconButton>

      <Box className="mainCarousel">
        <Carousel
          ref={modalCarouselRef}
          responsive={{
            desktop: { breakpoint: { max: 3000, min: 0 }, items: 1 },
          }}
          swipeable={false}
          draggable={false}
          showDots={false}
          keyBoardControl
          infinite={false}
          arrows
          afterChange={handleAfterChange}
        >
          {mediaArray.map((media, index) => {
            const isVideo = !!media.videoUrl;
            return (
              <Box key={index} className="mediaContainer">
                {isVideo ? (
                  <ReactPlayer
                    src={media.videoUrl}
                    playing={false}
                    controls
                    width="100%"
                    height="100%"
                  />
                ) : (
                  <TransformWrapper
                    ref={(el) => (transformRefs.current[index] = el)}
                    initialScale={1}
                    minScale={0.5} // Allow slight zoom out from 1
                    maxScale={5}
                    wheel={{ step: 0.2 }}
                    disablePadding={true}
                    doubleClick={{ mode: "zoomIn" }}
                    // Apply rotation logic using the state
                    // Disabling pan/pinch/wheel when rotation is not 0 or 180 to avoid weird behavior
                    // Customize as needed
                    disabled={false}
                  >
                    {({ zoomIn, zoomOut, resetTransform }) => (
                      <Box className="imageWrapper">
                        <Box className="zoomControls">
                          <IconButton
                            onClick={() => zoomIn()}
                            className="zoomButton"
                          >
                            <ZoomInIcon sx={{ fontSize: 30 }} />
                          </IconButton>
 
                          <IconButton
                            onClick={() => zoomOut()}
                            className="zoomButton"
                          >
                            <ZoomOutIcon sx={{ fontSize: 30 }} />
                          </IconButton>

                          {/* New Rotate Button */}
                          <IconButton
                            onClick={handleRotate}
                            className="rotateButton"
                          >
                            <RotateRightIcon sx={{ fontSize: 30 }} />
                          </IconButton>
 
                          <IconButton
                            onClick={() => resetTransform()}
                            className="resetButton"
                          >
                            Reset
                          </IconButton>
                        </Box>

                        <TransformComponent>
                            <Box
                                className="pujaImgBlock"
                                // Manually apply the rotation transform if needed, though initialRotation should handle it.
                                // Applying it directly on TransformComponent or its child might be necessary depending on library version/behavior.
                                style={{ transform: `rotate(${rotations[index]}deg)` }}
                            >
                              <ImageView
                                src={media.pictureUrl || PUJALOADIMG}
                                alt={`Media ${index}`}
                              />
                            </Box>
                        </TransformComponent>
                      </Box>
                    )}
                  </TransformWrapper>
                )}
              </Box>
            );
          })}
        </Carousel>

        <Box className="thumbnailContainer">
          {mediaArray.map((media, index) => {
            const isVideo = !!media.videoUrl;
            const isActive = currentIndex === index;
            return (
              <Box
                key={index}
                onClick={() => handleThumbnailClick(index)}
                className={`thumbnail ${isActive ? "active" : ""}`}
              >
                {isVideo ? (
                  <video src={media.videoUrl} />
                ) : (
                  <ImageView
                    src={media.pictureUrl || PUJALOADIMG}
                    alt={`Thumbnail ${index}`}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Dialog>
  );
};

export default ImageModalCarousel;